import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Row, createColumnHelper } from "@tanstack/react-table";
import {
  AuthConfig,
  ThreatHuntQueries,
  ThreatIntelComplete,
  ThreatIntelFailed,
  ThreatIntelOutput,
  ThreatIntelRunning,
  TimeFormats,
  addDays,
  formatDate,
  formatFileName,
  pluralize,
  queries,
  queryClient,
  select,
  service,
  timezoneToUtc,
  useAuthConfig,
  useCompile,
  useGenerateTechnique,
} from "@theprelude/core";
import {
  ArrowRight,
  Button,
  CheckmarkIcon,
  CloseIcon,
  CodeIcon,
  DateInput,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  InfoIcon,
  Input,
  InputField,
  Loading,
  MoreIcon,
  ProgressBar,
  RestartIcon,
  ScrollAreaRoot,
  ScrollAreaViewport,
  ScrollBar,
  SignalIcon,
  TargetIcon,
  Truncate,
  notifyError,
  notifySuccess,
  useDownloadFile,
} from "@theprelude/ds";
import * as zip from "@zip.js/zip.js";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { v4 } from "uuid";
import { z } from "zod";
import { shallow } from "zustand/shallow";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { CompileResults } from "@/components/compile-results/compile-results";
import { ThreatHuntDeepLink } from "@/components/deep-links";
import { DefaultTooltip } from "@/components/default-tooltip";
import Editor from "@/components/editor/editor";
import { Flag, hasFeatures } from "@/components/flag/flag";
import { orderColumn, techniqueColumn } from "@/components/table/columns";
import { useTable } from "@/components/table/hooks";
import { VirtualTable } from "@/components/table/virtual-table";
import ThreatImage from "@/components/threat-intel/threat-image";
import {
  ThreatIntelEdit,
  useThreatIntelStore,
} from "@/hooks/threat-intel-store";
import { usePopper } from "@/hooks/use-popper";
import { authorizeFeatures } from "@/lib/authorize-feature";

function generateRandomNumber(min: number, max: number) {
  return Math.round(Math.random() * (max - min) + min);
}

const getExpirationDate = () => timezoneToUtc(addDays(new Date(), 1));

const threatSchema = z.object({
  source_id: z.string().optional(),
});

export const Route = createFileRoute("/_app/threat-intel/$id")({
  loaderDeps: ({ search }) => {
    const randomBackgroundNumber = generateRandomNumber(1, 6);
    const randomElementNumber = generateRandomNumber(1, 7);
    return {
      randomBackgroundNumber,
      randomElementNumber,
      sourceId: search.source_id,
    };
  },
  loader: async (opts) => {
    setPageTitle("Threat Intel");
    await authorizeFeatures(opts.context, ["detect", "threat_intel"]);
  },
  validateSearch: threatSchema,
  pendingComponent: ThreatIntelLoading,
  component: ThreatIntelIdPage,
});

type ThreatIntelEntry = {
  order: number;
  ai_generated: boolean;
  id: string;
  name: string;
  technique: string;
  go_code: string;
  sigma_rules: string[];
  intel_context?: string;
  threat_hunt_queries: ThreatHuntQueries[];
};

function ActionsColumn({ row }: { row: Row<ThreatIntelEntry> }) {
  return (
    <div className="flex w-full items-center justify-end gap-2">
      <div className="size-4">
        {row.original.go_code && (
          <Dialog>
            <DefaultTooltip asChild content="View source code">
              <DialogTrigger asChild>
                <IconButton type="button" icon={<CodeIcon />} />
              </DialogTrigger>
            </DefaultTooltip>
            <DialogContent
              className="max-w-fit"
              onOpenAutoFocus={(event) => {
                event.preventDefault();
              }}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              <DialogHeader>
                <DialogTitle>{row.original.name}</DialogTitle>
              </DialogHeader>
              <DialogBody className="max-h-screen">
                <SourceCodeViewer row={row} />
              </DialogBody>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <Flag features={["detections"]}>
        <div className="size-4">
          {row.original.sigma_rules.length > 0 && (
            <Dialog>
              <DefaultTooltip asChild content="View detections">
                <DialogTrigger asChild>
                  <IconButton type="button" icon={<SignalIcon />} />
                </DialogTrigger>
              </DefaultTooltip>
              <DialogContent
                className="max-w-fit"
                onOpenAutoFocus={(event) => {
                  event.preventDefault();
                }}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                }}
              >
                <DialogHeader>
                  <DialogTitle>{row.original.name}</DialogTitle>
                </DialogHeader>
                <DialogBody className="max-h-screen">
                  <ScrollAreaRoot>
                    <ScrollAreaViewport className="max-h-[75vh]">
                      <div className="flex flex-col gap-4">
                        {row.original.sigma_rules.map((d, index) => (
                          <DetectionsViewer
                            index={index}
                            key={index}
                            row={row}
                            detection={d}
                          />
                        ))}
                      </div>
                    </ScrollAreaViewport>
                    <ScrollBar />
                  </ScrollAreaRoot>
                </DialogBody>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </Flag>

      <Flag features={["threat_intel"]}>
        <div className="size-4">
          {row.original.threat_hunt_queries.length > 0 && (
            <Dialog>
              <DefaultTooltip asChild content="View threat hunt queries">
                <DialogTrigger asChild>
                  <IconButton type="button" icon={<TargetIcon />} />
                </DialogTrigger>
              </DefaultTooltip>
              <DialogContent
                className="max-w-fit"
                onOpenAutoFocus={(event) => {
                  event.preventDefault();
                }}
                onCloseAutoFocus={(event) => {
                  event.preventDefault();
                }}
              >
                <DialogHeader>
                  <DialogTitle>{row.original.name}</DialogTitle>
                </DialogHeader>
                <DialogBody className="max-h-screen">
                  <ScrollAreaRoot>
                    <ScrollAreaViewport className="max-h-[75vh]">
                      <div className="flex flex-col gap-4">
                        {row.original.threat_hunt_queries.map((d, index) => (
                          <ThreatHuntQueryViewer
                            index={index}
                            key={index}
                            row={row}
                            threatHunt={d}
                          />
                        ))}
                      </div>
                    </ScrollAreaViewport>
                    <ScrollBar />
                  </ScrollAreaRoot>
                </DialogBody>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </Flag>

      <div className="size-4">
        <RegenerateActionItem row={row} />
      </div>
    </div>
  );
}

interface RegenerateForm {
  intel_context: string;
}

const RegenerateActionItem: React.FC<{ row: Row<ThreatIntelEntry> }> = ({
  row,
}) => {
  const params = Route.useParams();
  const regenerateTechnique = useGenerateTechnique();
  const clearEdits = useThreatIntelStore((state) => state.clearEdits);
  const dialog = usePopper();

  const { watch, register, handleSubmit, reset } = useForm<RegenerateForm>({
    defaultValues: {
      intel_context: row.original.intel_context ?? "",
    },
  });

  const submit = (data: RegenerateForm) => {
    regenerateTechnique.mutate(
      {
        technique: row.original.technique,
        parent_job_id: params.id,
        intel_context: data.intel_context,
      },
      {
        onSuccess: () => {
          notifySuccess({
            message: "Successfully sent regeneration request",
          });
          clearEdits(`${params.id}_${row.original.technique}`);
          dialog.close();
        },
        onError: (error) =>
          notifyError({
            message: "Regeneration request failed",
            error,
            dismissible: true,
          }),
      },
    );
  };

  return (
    <Dialog
      open={dialog.open}
      onOpenChange={(value) => {
        if (!value) {
          void reset();
          void dialog.close();
        }
      }}
    >
      <DefaultTooltip asChild content="More options">
        <Dropdown>
          <DropdownTrigger asChild>
            {<IconButton icon={<MoreIcon />} />}
          </DropdownTrigger>
          <DropdownContent align="end">
            <DialogTrigger asChild>
              <DropdownItem onSelect={() => dialog.on()}>
                Regenerate technique
              </DropdownItem>
            </DialogTrigger>
          </DropdownContent>
        </Dropdown>
      </DefaultTooltip>
      <DialogContent
        className="w-[500px]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Regenerate {row.original.technique}</DialogTitle>
        </DialogHeader>
        <form>
          <DialogBody className="max-h-screen pb-0">
            <InputField label={"Provide context for this technique"} required>
              {/* There is a 500 character limit to reduce high usage of tokens from OpenAI */}
              <textarea
                {...register("intel_context", { maxLength: 500 })}
                placeholder={"Enter context for this technique"}
                spellCheck={false}
                maxLength={500}
                className="h-40 w-full resize-none rounded border border-stroke-input bg-surface-background px-3 py-2 text-sm focus:border-primary-foreground focus:outline-none"
              />
            </InputField>
            <span className="flex justify-end pt-2 text-sm text-secondary-foreground">
              {500 - watch("intel_context").length} characters left
            </span>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button intent="secondary">Cancel</Button>
            </DialogClose>
            <Button
              intent="primary"
              loading={regenerateTechnique.isPending}
              disabled={regenerateTechnique.isPending}
              onClick={handleSubmit(submit)}
            >
              Regenerate
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const columnHelper = createColumnHelper<ThreatIntelEntry>();

const columns = [
  columnHelper.accessor("order", orderColumn),
  columnHelper.accessor("name", {
    cell: (info) => (
      <Truncate text={info.getValue()}>{info.getValue()}</Truncate>
    ),
    meta: {
      widths: ["180px", "3fr"],
      label: "Verified Security Test",
    },
  }),
  columnHelper.accessor("technique", techniqueColumn),
  columnHelper.display({
    id: "actions",
    cell: (info) => <ActionsColumn row={info.row} />,
    enableSorting: false,
    meta: {
      widths: ["112px", "112px"],
    },
  }),
];

function getExistingEdit(edits: ThreatIntelEdit[], name: string) {
  return edits.find((edit) => edit.name === name);
}

const formatData = (
  data: ThreatIntelOutput[],
  edits: ThreatIntelEdit[],
  jobId: string,
): ThreatIntelEntry[] => {
  return data.map((d, index) => {
    const originalGoCode =
      d.existing_test?.go_code ?? d.ai_generated?.go_code ?? "";
    const originalSigmaRules =
      d.existing_test?.sigma_rules ?? d.ai_generated?.sigma_rules ?? [];
    const originalThreatHuntQueries =
      d.existing_test?.threat_hunt_queries ??
      d.ai_generated?.threat_hunt_queries ??
      [];

    const go_code =
      getExistingEdit(edits, `${jobId}_${d.technique}_go_code`)?.content ??
      originalGoCode;

    const sigma_rules = originalSigmaRules.map(
      (rule, ruleIndex) =>
        getExistingEdit(
          edits,
          `${jobId}_${d.technique}_sigma_rules_${ruleIndex}`,
        )?.content ?? rule,
    );

    const threat_hunt_queries = originalThreatHuntQueries.map(
      (threat_hunt, index) => {
        const content =
          getExistingEdit(
            edits,
            `${jobId}_${d.technique}_threat_hunts_${index}`,
          )?.content ?? threat_hunt.query;

        return { ...threat_hunt, query: content };
      },
    );

    return {
      order: index + 1,
      ai_generated: Boolean(d.ai_generated),
      id: d.existing_test?.test_id ?? v4(),
      name: d.existing_test?.test_name ?? d.name,
      technique: d.technique,
      go_code,
      sigma_rules,
      intel_context: d.ai_generated?.intel_context,
      threat_hunt_queries,
    };
  });
};

function ThreatIntelIdPage() {
  const authConfig = useAuthConfig();
  const params = Route.useParams();
  const intel = useSuspenseQuery({
    ...queries.generate.getThreatIntel(authConfig, params.id),
    refetchInterval: (query) => {
      if (
        query.state.data?.status === "COMPLETE" ||
        query.state.data?.status === "FAILED"
      ) {
        return false;
      }
      return 1500;
    },
  });

  return (
    <div className="flex items-start p-8 sm:pb-16">
      {intel.data?.status === "RUNNING" && (
        <ThreatIntelRunningContent intel={intel.data} />
      )}
      {intel.data?.status === "COMPLETE" && (
        <ThreatIntelSuccessContent intel={intel.data} />
      )}
      {intel.data?.status === "FAILED" && (
        <ThreatIntelFailureContent intel={intel.data} />
      )}
    </div>
  );
}

function ThreatIntelLoading() {
  const deps = Route.useLoaderDeps();
  return (
    <div className="flex items-start p-8 sm:pb-16">
      <div className="mx-auto flex flex-col items-center gap-4 pt-8">
        <ThreatImage
          bgNumber={deps.randomBackgroundNumber}
          elNumber={deps.randomElementNumber}
        />
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-2 text-base">
            <Loading />
          </div>
        </div>
      </div>
    </div>
  );
}

const ThreatIntelRunningContent: React.FC<{
  intel: ThreatIntelRunning;
}> = ({ intel }) => {
  const deps = Route.useLoaderDeps();
  return (
    <div className="mx-auto flex flex-col items-center gap-4 pt-8">
      <ThreatImage
        bgNumber={deps.randomBackgroundNumber}
        elNumber={deps.randomElementNumber}
      />
      <div className="flex flex-col items-center gap-4 text-base">
        {intel.step === "GENERATE" ? (
          <ProgressBar
            value={((intel.completed_tasks + 1) / (intel.num_tasks + 1)) * 100}
          />
        ) : (
          <div className="h-[2px]" />
        )}

        {intel.step === "PARSE" ? (
          <span className="animate-pulse">Analyzing content...</span>
        ) : intel.step === "GENERATE" ? (
          "Generating content..."
        ) : (
          "Loading..."
        )}
      </div>
    </div>
  );
};

const ThreatIntelFailureContent: React.FC<{
  intel: ThreatIntelFailed;
}> = ({ intel }) => {
  return (
    <div className="mx-auto flex w-[520px] flex-col items-start gap-4 sm:pt-24">
      <div className="flex items-center justify-center rounded-full border border-status-red p-3 text-status-red">
        <CloseIcon className="size-6" />
      </div>
      <span className="text-2xl">
        Error: {intel.reason ?? "Oops, something went wrong."}
      </span>
      <span className="text-sm">
        We could not process the provided threat intelligence. Try uploading a
        different threat intelligence document.
      </span>
      <Link to="/threat-intel">
        <Button intent="secondary">Back</Button>
      </Link>
    </div>
  );
};

const FormSchema = z.object({
  source_id: z.string(),
  threat_name: z.string(),
  published: z.string(),
});

const handleTestCreation = async (
  test: ThreatIntelEntry,
  authConfig: AuthConfig,
) => {
  const account = await queryClient.ensureQueryData(
    queries.iam.getAccount(authConfig),
  );
  await service(authConfig).build.createTest({
    name: test.name,
    unit: "response",
    technique: test.technique,
    testId: test.id,
    intel_context: test.intel_context,
    ai_generated: true,
  });
  const rules = hasFeatures(account, ["detections"])
    ? test.sigma_rules.map(async (rule) => {
        return service(authConfig).build.createDetection({
          testId: test.id,
          rule: rule,
        });
      })
    : [];
  const threatHunts = hasFeatures(account, ["threat_intel"])
    ? test.threat_hunt_queries.map(async (threatHunt) => {
        return service(authConfig).build.createThreatHunt({
          control: threatHunt.control,
          name: threatHunt.name,
          query: threatHunt.query,
          test_id: test.id,
        });
      })
    : [];
  await Promise.all([
    ...rules,
    ...threatHunts,
    service(authConfig).build.upload({
      testId: test.id,
      filename: `${test.id}.go`,
      data: test.go_code,
      ai_generated: true,
    }),
  ]);
  return test.id;
};

const useCreateTestMutation = () => {
  const authConfig = useAuthConfig();

  return useMutation({
    mutationFn: async ({ test }: { test: ThreatIntelEntry }) => {
      return handleTestCreation(test, authConfig);
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully created test",
        error,
        dismissible: true,
      }),
  });
};

const useCreateThreatMutation = () => {
  const clearEdits = useThreatIntelStore((state) => state.clearEdits);
  const navigate = useNavigate();
  const authConfig = useAuthConfig();
  const params = Route.useParams();
  return useMutation({
    mutationFn: async ({
      testIds,
      name,
      source_id,
      published,
    }: {
      testIds: string[];
      name: string;
      source_id?: string;
      published: string;
    }) => {
      const threat_id = v4();
      const tests = testIds.join(",");
      await service(authConfig).build.createThreat({
        name,
        published,
        id: threat_id,
        source_id,
        source: undefined,
        tests,
        ai_generated: true,
      });

      return threat_id;
    },
    onError: (error) =>
      notifyError({
        message: "Unsuccessfully created threat",
        error,
        dismissible: true,
      }),
    onSuccess: (id) => {
      void clearEdits(params.id);

      void navigate({
        to: "/dashboard/new-threats",
        search: {
          overlay: ["threat", { id }],
        },
        params: (prev) => prev,
      });
    },
    retry: 1,
    retryDelay: 2000,
  });
};

function SourceCodeViewer({ row }: { row: Row<ThreatIntelEntry> }) {
  const params = Route.useParams();
  const authConfig = useAuthConfig();
  const editName = `${params.id}_${row.original.technique}_go_code`;
  const [content, setContent] = useState<string>(row.original.go_code);
  const [compiled, setCompiled] = useState<boolean>(false);
  const edits = useThreatIntelStore((state) => state.edits);
  const { setEdit, resetEdit } = useThreatIntelStore(
    select("setEdit", "resetEdit"),
    shallow,
  );

  const isEditable = row.original.ai_generated;
  const compile = useCompile();

  const handleCodeChange = (value: string) => {
    if (value !== content) {
      setCompiled(false);
    }
    setContent(value);
  };

  const compileJobId = compile.data?.job_id;
  const compiledQuery = useQuery({
    ...queries.build.getCompile(authConfig, compileJobId as string),
    enabled: Boolean(compileJobId),
    refetchInterval: (query) => {
      if (
        query.state.data?.status === "COMPLETE" ||
        query.state.data?.status === "FAILED"
      ) {
        setCompiled(true);
        return false;
      }
      return 1000;
    },
  });

  // This hook ensures that the component's state is synchronized with the row's go_code property.
  // The property will change when "edit" states are updated.
  useEffect(() => {
    setContent(row.original.go_code);
  }, [row.original.go_code]);

  const isEdited = useMemo(() => {
    return (
      row.original.go_code !== content ||
      Boolean(getExistingEdit(edits, editName))
    );
  }, [row.original.go_code, content, edits, editName]);
  const failedCompile = compiledQuery.data?.results?.some(
    (e) => e.status === "FAILED",
  );

  const isCompiling =
    compile.isPending || compiledQuery.data?.status === "RUNNING";

  return (
    <div className="flex flex-col gap-6 pr-6">
      <div className="flex text-sm">
        <Editor
          onChange={handleCodeChange}
          value={content}
          editable={isEditable}
          type="go"
          width="75vw"
          height="60vh"
        />
      </div>

      {isEditable && (
        <div className="flex items-start justify-between">
          <div className="flex w-3/4 flex-col gap-1">
            <span className="text-xs text-secondary-foreground">
              {isEdited
                ? "This modified version of the code will overwrite the original on save."
                : ""}
            </span>
            {compiledQuery.data && <CompileResults data={compiledQuery.data} />}
          </div>

          <div className="flex items-center gap-2">
            <Button
              icon={<RestartIcon />}
              intent="secondary"
              onClick={() => {
                getExistingEdit(edits, editName)
                  ? resetEdit(editName)
                  : setContent(row.original.go_code);
              }}
              disabled={!isEdited}
            >
              Revert changes
            </Button>

            <Button
              loading={isCompiling}
              intent="primary"
              onClick={() =>
                compile.mutate(
                  {
                    code: content,
                  },
                  {
                    onError: (error) => {
                      notifyError({
                        message: "Error occured when attempting to compile",
                        error,
                      });
                    },
                  },
                )
              }
              disabled={isCompiling}
            >
              Compile
            </Button>

            <DefaultTooltip
              asChild
              content="Save edited code. Please compile code first"
            >
              <Button
                intent="primary"
                onClick={() => {
                  setEdit({
                    name: editName,
                    technique: row.original.technique,
                    content,
                    expiration: getExpirationDate().toString(),
                  });
                  notifySuccess({
                    message: "Successfully saved code",
                  });
                }}
                disabled={!isEdited || !compiled || failedCompile}
              >
                Save
              </Button>
            </DefaultTooltip>
          </div>
        </div>
      )}
    </div>
  );
}

function DetectionsViewer({
  index,
  detection,
  row,
}: {
  index: number;
  detection: string;
  row: Row<ThreatIntelEntry>;
}) {
  const params = Route.useParams();
  const editName = `${params.id}_${row.original.technique}_sigma_rules_${index}`;
  const [content, setContent] = useState<string>(detection);
  const { setEdit, resetEdit } = useThreatIntelStore(
    select("setEdit", "resetEdit"),
    shallow,
  );
  const edits = useThreatIntelStore((state) => state.edits);
  const isEditable = row.original.ai_generated;
  const isEdited = useMemo(() => {
    return detection !== content || Boolean(getExistingEdit(edits, editName));
  }, [detection, content, edits, editName]);

  // This hook ensures that the component's state is synchronized with the detection property.
  // The property will change when "edit" states are updated.
  useEffect(() => {
    setContent(detection);
  }, [detection]);

  return (
    <div className="flex flex-col gap-6 pr-6">
      <h2 className="text-lg">Custom Detection {index + 1}</h2>
      <div className="flex text-sm">
        <Editor
          onChange={setContent}
          value={content}
          editable={isEditable}
          type="yaml"
          width="75vw"
          height="55vh"
        />
      </div>
      {isEditable && (
        <div className="flex items-start justify-between">
          <span className="text-xs text-secondary-foreground">
            {isEdited
              ? "This modified version of the detection will overwrite the original on save."
              : ""}
          </span>
          <div className="flex gap-2">
            <Button
              icon={<RestartIcon />}
              intent="secondary"
              onClick={() => {
                getExistingEdit(edits, editName)
                  ? resetEdit(editName)
                  : setContent(detection);
              }}
              disabled={!isEdited}
            >
              Revert changes
            </Button>

            <Button
              intent="primary"
              onClick={() => {
                setEdit({
                  name: editName,
                  technique: row.original.technique,
                  content,
                  expiration: getExpirationDate().toString(),
                });
                notifySuccess({
                  message: "Successfully saved detection",
                });
              }}
              disabled={!isEdited}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ThreatHuntQueryViewer({
  index,
  threatHunt,
  row,
}: {
  index: number;
  threatHunt: ThreatHuntQueries;
  row: Row<ThreatIntelEntry>;
}) {
  const { query, name, control } = threatHunt;
  const params = Route.useParams();
  const editName = `${params.id}_${row.original.technique}_threat_hunts_${index}`;
  const [content, setContent] = useState<string>(query);
  const { setEdit, resetEdit } = useThreatIntelStore(
    select("setEdit", "resetEdit"),
    shallow,
  );
  const edits = useThreatIntelStore((state) => state.edits);
  const isEditable = row.original.ai_generated;
  const isEdited = useMemo(() => {
    return query !== content || Boolean(getExistingEdit(edits, editName));
  }, [query, content, edits, editName]);

  // This hook ensures that the component's state is synchronized with the query property.
  // The property will change when "edit" states are updated.
  useEffect(() => {
    setContent(query);
  }, [query]);

  return (
    <div className="flex flex-col gap-6 pr-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg">{name}</h2>
        <ThreatHuntDeepLink control={control} query={content} />
      </div>
      <div className="flex text-sm">
        <Editor
          onChange={setContent}
          value={content}
          editable={isEditable}
          width="75vw"
          height="55vh"
        />
      </div>
      {isEditable && (
        <div className="flex items-start justify-between">
          <span className="text-xs text-secondary-foreground">
            {isEdited
              ? "This modified version of the query will overwrite the original on save."
              : ""}
          </span>

          <div className="flex gap-2">
            <Button
              icon={<RestartIcon />}
              intent="secondary"
              onClick={() => {
                getExistingEdit(edits, editName)
                  ? resetEdit(editName)
                  : setContent(query);
              }}
              disabled={!isEdited}
            >
              Revert changes
            </Button>

            <Button
              intent="primary"
              onClick={() => {
                setEdit({
                  name: editName,
                  technique: row.original.technique,
                  content,
                  expiration: getExpirationDate().toString(),
                });
                notifySuccess({
                  message: "Successfully saved query",
                });
              }}
              disabled={!isEdited}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

type Manifest = {
  tests: { id: string; name: string; technique: string }[];
};

function getExcludedTechniques(
  skippedOutput: ThreatIntelOutput[],
  failedOutput: ThreatIntelOutput[],
) {
  const excludedSkipped = skippedOutput.filter(
    (o) =>
      o.excluded?.classification === "EXCLUDED" ||
      o.excluded?.classification === "TOO MANY TECHNIQUES",
  );

  return [...failedOutput, ...excludedSkipped];
}

const ThreatIntelSuccessContent: React.FC<{
  intel: ThreatIntelComplete;
}> = ({ intel }) => {
  const params = Route.useParams();
  const deps = Route.useLoaderDeps();
  const { register, handleSubmit, control } = useForm<
    z.infer<typeof FormSchema>
  >({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      threat_name: intel.title ?? undefined,
      source_id: deps.sourceId,
      published: formatDate(new Date(), TimeFormats.DATE_SHORT),
    },
  });
  const edits = useThreatIntelStore((state) => state.edits);

  const successfulOutput = useMemo(
    () => intel.output.filter((o) => o.status === "SUCCEEDED") ?? [],
    [intel.output],
  );

  const failedOutput = useMemo(
    () => intel.output.filter((o) => o.status === "FAILED") ?? [],
    [intel.output],
  );

  const skippedOutput = useMemo(
    () => intel.output.filter((o) => o.status === "SKIPPED") ?? [],
    [intel.output],
  );

  const createTest = useCreateTestMutation();
  const createThreat = useCreateThreatMutation();

  const zipName = intel.title ? formatFileName(intel.title) : `threat_output`;

  const data = useMemo(() => {
    if (successfulOutput.length === 0) {
      return [];
    }
    return formatData(successfulOutput, edits, params.id);
  }, [successfulOutput, edits, params.id]);

  const existingTests = data.filter((d) => !d.ai_generated);
  const existingTestsIds = existingTests.map((e) => e.id);
  const aiGeneratedTests = data.filter((d) => d.ai_generated);

  const excludedTechniques = getExcludedTechniques(skippedOutput, failedOutput);
  const buttonText = createThreat.isPending
    ? "Creating threat..."
    : createTest.isPending
      ? "Creating tests..."
      : "Import threat to Detect";

  const downloadFiles = useDownloadFile({
    async data() {
      const zipWriter = new zip.ZipWriter(
        new zip.BlobWriter("application/zip"),
      );
      const manifest: Manifest = {
        tests: [],
      };

      // Define the root folder path
      const rootFolderPath = `${zipName}/`;

      for (const {
        id,
        go_code,
        sigma_rules,
        threat_hunt_queries,
        name,
        technique,
      } of data) {
        if (!sigma_rules || !go_code || !threat_hunt_queries) {
          throw new Error("No output data found");
        }

        manifest.tests.push({ id, name, technique });

        // Define the test folder path
        const testFolderPath = `${rootFolderPath}${technique}_${formatFileName(name)}/`;

        // Add .go file to the test folder
        await zipWriter.add(
          `${testFolderPath}${id}.go`,
          new zip.TextReader(go_code),
        );

        // Add .yml files to the test folder
        for (const [index, rule] of sigma_rules.entries()) {
          await zipWriter.add(
            `${testFolderPath}${id}_detection_${index + 1}.yml`,
            new zip.TextReader(rule),
          );
        }

        // Add .txt files to the test folder
        for (const [index, threat_hunt] of threat_hunt_queries.entries()) {
          await zipWriter.add(
            `${testFolderPath}${id}_threat_hunt_${index + 1}.txt`,
            new zip.TextReader(threat_hunt.query),
          );
        }
      }

      // Add the manifest.json file at the root level
      await zipWriter.add(
        `${rootFolderPath}manifest.json`,
        new zip.TextReader(JSON.stringify(manifest, null, 2)),
      );
      return zipWriter.close();
    },
    filename: `${zipName}.zip`,
    type: "application/zip",
    description: "Zip file containing threat intel",
    onSuccess: () => {
      notifySuccess({
        message: "Succesfully downloaded threat intel",
      });
    },
    onError: (err) => {
      notifyError({
        message: "Failed to download threat intel",
        error: err,
      });
    },
  });

  const handleCreate = async (values: z.infer<typeof FormSchema>) => {
    const tests = await Promise.allSettled(
      Array.from({ length: aiGeneratedTests.length }).map((_, index) => {
        const test = aiGeneratedTests[index];
        if (!test) {
          throw new Error("Output not found");
        }
        return createTest.mutateAsync(
          { test },
          {
            onSuccess: () => {
              notifySuccess({
                message: "Successfully created tests",
                dismissible: true,
              });
            },
          },
        );
      }),
    );

    const successfulTestIds: string[] = existingTestsIds;

    for (const test of tests) {
      if (test.status === "fulfilled" && test.value) {
        successfulTestIds.push(test.value);
      }
    }

    createThreat.mutate({
      testIds: successfulTestIds,
      name: values.threat_name,
      source_id: values.source_id,
      published: values.published,
    });
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(handleCreate)}
        className="flex w-full flex-col items-start justify-center gap-8 xl:flex-row xl:pt-24"
      >
        <div className="flex flex-col items-start gap-4 sm:w-[520px]">
          <div className="flex items-center justify-center rounded-full border border-status-green p-3 text-status-green">
            <CheckmarkIcon className="size-6" />
          </div>
          <span className="text-2xl">
            Your tests and custom detections are ready.
          </span>
          <span className="text-sm">
            Please review the tests and custom detections generated based on the
            threat information you provided. Once you import this threat into
            Detect, you can schedule it to run in your environment. We suggest
            scheduling this threat on a limited number of endpoints to start.
          </span>
          <Button
            type="submit"
            intent="primary"
            icon={<ArrowRight />}
            iconPosition="right"
            loading={createTest.isPending || createThreat.isPending}
          >
            {buttonText}
          </Button>
        </div>
        <div className="flex flex-col items-start gap-4 border border-stroke-line bg-surface-background p-5 sm:w-[600px] sm:p-10">
          <div className="flex w-full flex-col gap-6">
            <div className="flex w-[199px] flex-col gap-6 sm:w-full sm:flex-row sm:items-center">
              <InputField label="Publish Date" required>
                <Controller
                  name="published"
                  control={control}
                  render={({ field }) => <DateInput {...field} />}
                />
              </InputField>

              <InputField label="Source ID">
                <Input
                  type="text"
                  {...register("source_id")}
                  placeholder="(ex: 'AA24-016A')"
                />
              </InputField>
            </div>

            <InputField label="Threat Name" required>
              <Input
                type="text"
                {...register("threat_name", { required: true })}
                placeholder="My threat name"
                required
              />
            </InputField>
          </div>

          <span className="text-xs text-shingle-grey">
            {data.length} {pluralize(data.length, "test")} and custom{" "}
            {pluralize(data.length, "detection")} to review
          </span>

          <TableView data={data} />

          {excludedTechniques.length > 0 && (
            <div className="flex items-center gap-2 text-secondary-foreground">
              <InfoIcon />
              <span>Some techniques were excluded from this threat.</span>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    className="p-0 underline underline-offset-2"
                    type="button"
                  >
                    More info
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Excluded Techniques</DialogTitle>
                  </DialogHeader>
                  <DialogBody className="max-h-screen">
                    <ScrollAreaRoot className="mt-4 bg-tertiary-foreground">
                      <ScrollAreaViewport className="max-h-[60vh]">
                        <div className="flex flex-col gap-4 p-4">
                          {excludedTechniques.map((e) => (
                            <div className="flex flex-col" key={e.technique}>
                              <span>{e.technique}: Excluded</span>
                              <span>
                                Reason: Excluded due to safety concerns
                              </span>
                            </div>
                          ))}
                        </div>
                      </ScrollAreaViewport>
                      <ScrollBar />
                    </ScrollAreaRoot>
                  </DialogBody>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <Button
            type="button"
            intent="secondary"
            loading={downloadFiles.pending}
            onClick={() => {
              void downloadFiles.execute();
            }}
          >
            Download Threat Output
          </Button>
        </div>
      </form>
    </>
  );
};

function TableView({ data }: { data: ThreatIntelEntry[] }) {
  const table = useTable({
    name: "threat-intel",
    data,
    columns,
    initialState: {
      sorting: [{ id: "order", desc: false }],
    },
  });

  return <VirtualTable className="max-h-64" table={table} />;
}
